import { useRef, useCallback } from 'react';

/**
 * Backdrop click handler odporny na "selection-end-outside" - czyli sytuację
 * gdy user zaznacza tekst wewnątrz modala i kończy zaznaczenie poza nim.
 * Default `onClick` triggeruje wtedy fałszywe zamknięcie (mouseup ląduje na
 * backdropie). Hook śledzi gdzie był MOUSEDOWN i zamyka tylko gdy *cały*
 * gest (down + up) nastąpił na backdropie.
 *
 * Dotyczy wszystkich modali w aplikacji — patrz product-decisions.md
 * "Modal close-on-outside ignoruje selection-end-outside".
 *
 * Użycie:
 *   const backdropProps = useBackdropClickClose(onClose);
 *   <div className="backdrop" {...backdropProps}>
 *     <div className="modal-content" onClick={e => e.stopPropagation()}>...</div>
 *   </div>
 */
export function useBackdropClickClose(onClose: () => void, enabled = true) {
  const downOnBackdropRef = useRef(true);

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLElement>) => {
    // Backdrop = currentTarget. Gdy mousedown trafił na coś innego (np. modal
    // content), ustawiamy flagę false — wtedy mouseup na backdropie nie zamknie.
    downOnBackdropRef.current = e.target === e.currentTarget;
  }, []);

  const onClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    if (!enabled) return;
    if (downOnBackdropRef.current && e.target === e.currentTarget) {
      onClose();
    }
  }, [enabled, onClose]);

  return { onMouseDown, onClick };
}
